import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, Subject, of, from } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { IApplication, IApplicationResponse } from '../models/application.model';
import { UserManager } from 'oidc-client';
import { AppConfig } from './app.config';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  private appUrl = 'api/application';
  private apps: IApplication[] = [];
  private _userManager: UserManager;

  constructor(private http: HttpClient) {

    const stsSettings = {
      authority: AppConfig.settings.ID4.AuthAddress,
      client_id: AppConfig.settings.ID4.Client,
      redirect_uri: AppConfig.settings.ID4.Redirect,
      scope: 'openid RNapi',
      response_type: 'code',
      post_logout_redirect_uri: AppConfig.settings.ID4.Post_Logout,
      loadUserInfo: false
    };

    this._userManager = new UserManager(stsSettings);

  }

  setApps(val: IApplication[]) {
    this.apps = val;
  }

  checkApplication(appID, appName) {
    return new Promise(resolve => {
      if (!(this.apps && this.apps.length >= 1)) {
        this.getApplications().subscribe({
          next: response => {
            this.apps = response;
            resolve((this.apps.findIndex((a) => a.applicationId === appID && a.appName === appName) > -1));
          },
        });
      } else {
        resolve((this.apps.findIndex((a) => a.applicationId === appID && a.appName === appName) > -1));
      }
    });
  }

  getApplications(isActive: boolean = true): Observable<IApplication[]> {
    const url = `${this.appUrl}/active/${isActive}`;
    if (this.apps && this.apps.length >= 1) {
      return of(this.apps);
    } else {
      return this.http.get<IApplication[]>(url, { withCredentials: false })
        .pipe(tap(data => {}), catchError(this.handleError));
    }
  }

  getAllApplications(): Observable<IApplication[]> {
    if (this.apps && this.apps.length >= 1) {
      return of(this.apps);
    } else {
      return this.http.get<IApplication[]>(this.appUrl, { withCredentials: false })
        .pipe(tap(data => {}), catchError(this.handleError));
    }
  }

  setApplication(appForm): Observable<IApplicationResponse> {
    return from(this._userManager.getUser().then(user => {
      if (!(user == null) && !user.expired) {
        const head = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.access_token}`
        });
        return this.http.post<any>(this.appUrl, appForm, { headers: head }).toPromise();
      }
      else {
        this._userManager.signinRedirect();
      }
    }));
  }

  updateApplication(appForm): Observable<IApplicationResponse> {
    return from(this._userManager.getUser().then(user => {
      if (!(user == null) && !user.expired) {
        const head = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.access_token}`
        });
        return this.http.patch<any>(this.appUrl, appForm, { headers: head }).toPromise();
      }
      else {
        this._userManager.signinRedirect();
      }
    }));
  }

  private handleError(err: HttpErrorResponse) {
    let errorMessage = '';
    if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }

}
